import { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Map, 
  BookOpen, 
  Image, 
  Settings, 
  LogOut, 
  Menu, 
  Users,
  X,
  MessageSquare
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const sidebarItems = [
  { icon: LayoutDashboard, label: "Dashboard", href: "/admin-panel", permission: "dashboard" },
  { icon: Map, label: "Circuits", href: "/admin-panel/circuits", permission: "circuits" },
  { icon: BookOpen, label: "Stories", href: "/admin-panel/stories", permission: "stories" },
  { icon: Image, label: "Gallery", href: "/admin-panel/gallery", permission: "gallery" },
  { icon: Map, label: "Tour Plans", href: "/admin-panel/tour-plans", permission: "tour_plans" },
  { icon: MessageSquare, label: "Inquiries", href: "/admin-panel/inquiries", permission: "inquiries" },
  { icon: Settings, label: "Metadata & Settings", href: "/admin-panel/metadata", permission: "metadata" },
  { icon: Users, label: "Admin Users", href: "/admin-panel/admins", permission: "admins" },
  { icon: BookOpen, label: "Legal Pages", href: "/admin-panel/legal", permission: "legal" },
];

export const AdminLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_login_timestamp");
    localStorage.removeItem("username");
    localStorage.removeItem("is_superuser");
    localStorage.removeItem("admin_profile_image");
    localStorage.removeItem("admin_permissions");
    navigate("/admin-panel/login");
  };

  const hasPermission = (permission: string) => {
      // Superuser has all permissions
      if (localStorage.getItem("is_superuser")) return true;
      
      const stored = localStorage.getItem("admin_permissions");
      if (!stored) return false;
      
      try {
          const permissions = JSON.parse(stored);
          return Array.isArray(permissions) && permissions.includes(permission);
      } catch (e) {
          return false;
      }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-auto",
          isSidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex h-16 items-center justify-center border-b border-border">
          <span className="text-xl font-serif font-bold text-primary">Baelleaves Admin</span>
        </div>
        
        <nav className="p-4 space-y-2">
          {sidebarItems.map((item) => {
             if (!hasPermission(item.permission)) {
                 return null;
             }
             return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                location.pathname === item.href
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          )})}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
          <Button variant="ghost" className="w-full justify-start gap-3 text-muted-foreground hover:text-destructive" onClick={handleLogout}>
            <LogOut className="w-5 h-5" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 border-b border-border bg-card flex items-center justify-between px-6">
          <button
            className="lg:hidden p-2 text-muted-foreground"
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          >
            {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <div className="ml-auto flex items-center gap-4">
             <span className="text-sm text-muted-foreground">
               Welcome, {localStorage.getItem("name") || localStorage.getItem("username")?.split('@')[0] || "Admin"}
             </span>
             {localStorage.getItem("admin_profile_image") ? (
                 <img src={localStorage.getItem("admin_profile_image")!} alt="Profile" className="w-8 h-8 rounded-full object-cover border border-border" loading="lazy" />
             ) : (
             <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
               {(localStorage.getItem("name") || localStorage.getItem("username")?.split('@')[0] || "A").charAt(0).toUpperCase()}
             </div>
             )}
          </div>
        </header>

        {/* Page Content */}
        <div className="flex-1 overflow-auto p-6 md:p-8">
          <Outlet />
        </div>
      </main>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};
